import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { WishListItem } from '../schema';
import { useState, useEffect } from 'react';
import { db } from '../instant';

interface Props {
    wishListItem: WishListItem;
    packingListQuantity: number;
    onRemove: () => void;
}

export default function WishListItemRow({ wishListItem, packingListQuantity, onRemove }: Props) {
    const itemType = wishListItem.itemType;
    const category = itemType?.category;
    const quantity = wishListItem.quantity ?? 0;
    const minQuantity = -packingListQuantity;
    const [localInput, setLocalInput] = useState(String(quantity));
    const [isSaving, setIsSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Sync local input when wishListItem or min changes
    useEffect(() => {
        setLocalInput(String(quantity));
    }, [quantity]);

    const categoryColor = category?.color || '#2196F3';

    return (
        <TableRow>
            {/* Photo */}
            <TableCell>
                <Avatar
                    src={itemType?.photoUrl || undefined}
                    variant="rounded"
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'grey.200',
                    }}
                >
                    {!itemType?.photoUrl && <LocalOfferIcon />}
                </Avatar>
            </TableCell>

            {/* Name + Category */}
            <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {itemType?.name || 'Unknown Item'}
                    </Typography>
                    {category && (
                        <Chip
                            label={category.name}
                            size="small"
                            sx={{
                                bgcolor: categoryColor,
                                color: 'white',
                                fontSize: '0.7rem',
                                width: 'fit-content',
                            }}
                        />
                    )}
                </Box>
            </TableCell>

            {/* Quantity Input */}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        type="number"
                        value={localInput}
                        onChange={e => {
                            setLocalInput(e.target.value);
                            setError(null);
                        }}
                        onBlur={async () => {
                            const val = parseInt(localInput, 10);
                            const isValid = Number.isInteger(val) && val >= minQuantity;
                            const newQuantity = isValid ? val : quantity;

                            if (newQuantity === quantity) {
                                setLocalInput(String(quantity));
                                return;
                            }

                            if (!isValid) {
                                setLocalInput(String(quantity));
                                return;
                            }

                            setIsSaving(true);
                            setError(null);

                            try {
                                await db.transact([
                                    db.tx.wishListItems[wishListItem.id].update({
                                        quantity: newQuantity,
                                    }),
                                ]);
                                setLocalInput(String(newQuantity));
                            } catch (err) {
                                console.error('Error updating wish list item:', err);
                                setError('Failed to update quantity');
                                setLocalInput(String(quantity));
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                        inputProps={{
                            min: minQuantity,
                            style: { textAlign: 'center', width: '60px' },
                        }}
                        size="small"
                        sx={{ width: '80px' }}
                        error={!!error}
                        helperText={error ?? (packingListQuantity > 0 ? `Min ${minQuantity}` : undefined)}
                    />
                    {isSaving && <CircularProgress size={16} />}
                </Box>
            </TableCell>

            {/* Remove Button */}
            <TableCell>
                <IconButton size="small" color="error" onClick={() => setDeleteDialogOpen(true)}>
                    <DeleteIcon />
                </IconButton>
            </TableCell>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Remove from Wish List?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Remove &quot;{itemType?.name || 'Unknown Item'}&quot; from the wish list?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} autoFocus>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            try {
                                await db.transact([db.tx.wishListItems[wishListItem.id].delete()]);
                                setDeleteDialogOpen(false);
                                onRemove();
                            } catch (err) {
                                console.error('Error deleting wish list item:', err);
                                // TODO: Show error toast
                            }
                        }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </TableRow>
    );
}
